import { useCallback } from 'react'
import { isCypressWindow } from '@retailer-platform/shared-common'

// Persistence for the v3 nav onboarding tour. Mirrors the localStorage
// approach used by `useNavV3OptOutSurveyAccess.ts` — a per-browser signal
// covers the "first time you see v3" entry point without a backend touch.
//
// The stored value is versioned so future copy/UX changes can re-prompt the
// entire user base by bumping `NAV_V3_ONBOARDING_TOUR_VERSION`.
export const NAV_V3_ONBOARDING_TOUR_STORAGE_KEY = 'navV3OnboardingTour'
export const NAV_V3_ONBOARDING_TOUR_VERSION = '2'

type NavV3OnboardingTourStorage = {
  seenVersion: string
}

const readStorage = (): NavV3OnboardingTourStorage | null => {
  try {
    const raw = window.localStorage.getItem(NAV_V3_ONBOARDING_TOUR_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as NavV3OnboardingTourStorage
    return typeof parsed?.seenVersion === 'string' ? parsed : null
  } catch {
    return null
  }
}

const writeStorage = (value: NavV3OnboardingTourStorage) => {
  try {
    window.localStorage.setItem(NAV_V3_ONBOARDING_TOUR_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // localStorage can throw in private browsing / quota-exceeded scenarios.
    // Failing silently is fine: worst case we re-show the tour next time.
  }
}

// Returns a function that reports whether the auto-start should fire right
// now. Reads on every call so a value written from another tab is honored
// without needing a re-render.
//
// Suppressed under Cypress: e2e specs clear localStorage between runs, which
// would otherwise fire the tour on every test and overlay a portal popup
// (zIndex 10000) anchored to the sidebar home item over the page content the
// specs are interacting with. The tour is a pure welcome UX with no test
// coverage in e2e — manual re-trigger from the insights carousel still works.
export const useShouldAutoStartNavV3Onboarding = () =>
  useCallback(() => {
    if (isCypressWindow()) return false
    const seenVersion = readStorage()?.seenVersion
    return seenVersion !== NAV_V3_ONBOARDING_TOUR_VERSION
  }, [])

// Marks the current onboarding version as seen. Called when the auto-start
// fires (not when manual re-trigger from the carousel opens the tour).
export const useMarkNavV3OnboardingSeen = () =>
  useCallback(() => {
    writeStorage({ seenVersion: NAV_V3_ONBOARDING_TOUR_VERSION })
  }, [])
